import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import { fmtClock } from '../lib/format.js';

// "Report call-out" panel (sick-call coverage Phase 2, spec
// SICK-CALL-COVERAGE-SPEC.md). Manager flow, one linear pass:
//   pick who called out → their next-7-days published shifts → check the
//   ones they'll miss → Record (sick incidents, penalty 0) → per shift,
//   who can cover → Assign directly or Message-all-to-cover (first to
//   accept via the existing pickup flow gets it).
//
// Default-checked shifts: only the earliest shift date — a call-out is
// usually "I'm out today"; the manager extends the selection when the
// employee says they're out longer (the illness-cascade lookahead is
// there precisely so that's one tap, not a re-open tomorrow).

// api() throws with the raw response body as the message — unwrap the
// {"error": "..."} JSON the server sends so managers never see raw JSON.
function apiErr(e, fallback) {
  let msg = e.message || fallback;
  try { msg = JSON.parse(msg).error || msg; } catch {}
  return msg;
}

function dayLabel(ymd) {
  const d = new Date(ymd + 'T12:00:00Z');
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  return `${day} · ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

export function ReportCalloutModal({ currentUser, onClose }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [roster, setRoster] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [lookahead, setLookahead] = useState(null);     // { employee, shifts }
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [outShiftIds, setOutShiftIds] = useState(new Set());
  const [recorded, setRecorded] = useState(null);        // { recorded, skipped }
  const [recording, setRecording] = useState(false);
  const [cands, setCands] = useState({});                // shiftId → { loading, list, error }
  const [expanded, setExpanded] = useState(null);        // shiftId
  const [shiftDone, setShiftDone] = useState({});        // shiftId → status text
  const [busy, setBusy] = useState(null);                // `${shiftId}:${action}`
  const [err, setErr] = useState('');

  useEffect(() => {
    api('GET', '/api/users', null, headers)
      .then((rows) => setRoster((rows || []).filter((u) => u.id !== currentUser.id)))
      .catch(() => setRoster([]));
  }, []);

  // Guards against a slow lookahead response landing after the manager has
  // switched employees — without it, Record could post the previous person's
  // shift dates against the newly selected employee_id.
  const pickSeq = useRef(0);

  async function pickEmployee(id) {
    const seq = ++pickSeq.current;
    setEmployeeId(id);
    setLookahead(null); setRecorded(null); setCands({}); setExpanded(null); setShiftDone({}); setErr('');
    if (!id) return;
    setLoadingShifts(true);
    try {
      const r = await api('GET', `/api/coverage/callout/${id}`, null, headers);
      if (seq !== pickSeq.current) return; // superseded by a newer selection
      setLookahead(r);
      const first = r.shifts[0]?.shift_date;
      setOutShiftIds(new Set(r.shifts.filter((s) => s.shift_date === first).map((s) => s.id)));
    } catch (e) {
      if (seq === pickSeq.current) setErr(apiErr(e, 'Failed to load shifts'));
    }
    finally { if (seq === pickSeq.current) setLoadingShifts(false); }
  }

  function toggleShift(id) {
    setOutShiftIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const outShifts = (lookahead?.shifts || []).filter((s) => outShiftIds.has(s.id));
  const outDates = [...new Set(outShifts.map((s) => s.shift_date))];

  async function record() {
    setRecording(true); setErr('');
    try {
      const r = await api('POST', '/api/coverage/callout',
        { employee_id: Number(employeeId), dates: outDates }, headers);
      setRecorded(r);
    } catch (e) { setErr(apiErr(e, 'Failed to record call-out')); }
    finally { setRecording(false); }
  }

  async function loadCandidates(s) {
    if (expanded === s.id) { setExpanded(null); return; }
    setExpanded(s.id);
    if (cands[s.id]?.list) return;
    setCands((p) => ({ ...p, [s.id]: { loading: true } }));
    try {
      const q = `location_id=${s.location_id}&date=${s.shift_date}` +
        `&start_time=${s.start_time}&end_time=${s.end_time}&exclude_employee_id=${employeeId}` +
        (s.role ? `&role=${encodeURIComponent(s.role)}` : '');
      const r = await api('GET', `/api/coverage/candidates?${q}`, null, headers);
      setCands((p) => ({ ...p, [s.id]: { list: r.candidates } }));
    } catch (e) {
      setCands((p) => ({ ...p, [s.id]: { error: apiErr(e, 'Failed to load candidates') } }));
    }
  }

  async function assign(s, candidate) {
    setBusy(`${s.id}:assign`); setErr('');
    try {
      await api('PUT', `/api/shifts/${s.id}`, { employee_id: candidate.id }, headers);
      setShiftDone((p) => ({ ...p, [s.id]: `Assigned to ${candidate.name}` }));
      setExpanded(null);
    } catch (e) { setErr(apiErr(e, 'Assign failed')); }
    finally { setBusy(null); }
  }

  async function messageToCover(s) {
    const list = cands[s.id]?.list || [];
    setBusy(`${s.id}:message`); setErr('');
    try {
      const r = await api('POST', `/api/coverage/shifts/${s.id}/request-cover`,
        { candidate_ids: list.map((c) => c.id) }, headers);
      setShiftDone((p) => ({ ...p, [s.id]: `${r.notified} messaged — open for pickup, first to accept gets it` }));
      setExpanded(null);
    } catch (e) { setErr(apiErr(e, 'Message failed')); }
    finally { setBusy(null); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '85vh',
                     display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Report call-out</div>
          <div style={{ fontSize: 12, color: '#757575' }}>
            Sick days don't affect reliability. Recording one also stops a no-show from being logged.
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>
            Who called out?
            <select value={employeeId} onChange={(e) => pickEmployee(e.target.value)}
              style={{ width: '100%', marginTop: 4, padding: 8, fontFamily: 'inherit', fontSize: 14, border: '1px solid #d0d4d9', borderRadius: 6 }}>
              <option value="">— choose an employee —</option>
              {roster.map((u) => (
                <option key={u.id} value={u.id}>{u.name} · {u.role}</option>
              ))}
            </select>
          </label>

          {loadingShifts && <div style={{ fontSize: 12, color: '#757575' }}>Loading their shifts…</div>}

          {lookahead && lookahead.shifts.length === 0 && (
            <div style={{ fontSize: 13, color: '#757575' }}>
              No published shifts in the next 7 days — nothing to cover.
            </div>
          )}

          {lookahead && lookahead.shifts.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                Which shifts will {lookahead.employee.name.split(' ')[0]} miss?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {lookahead.shifts.map((s) => (
                  <div key={s.id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 10,
                                            background: outShiftIds.has(s.id) ? '#fffdf3' : '#fff' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input type="checkbox" checked={outShiftIds.has(s.id)} onChange={() => toggleShift(s.id)}
                        disabled={!!recorded} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#37474f' }}>
                          {dayLabel(s.shift_date)} · {fmtClock(s.start_time)}–{fmtClock(s.end_time)}
                        </div>
                        <div style={{ fontSize: 11, color: '#757575' }}>
                          {s.location_name}{s.role ? ` · ${s.role}` : ''}
                        </div>
                      </div>
                      {shiftDone[s.id] && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#2e7d32' }}>✓ {shiftDone[s.id]}</span>
                      )}
                    </label>

                    {recorded && outShiftIds.has(s.id) && !shiftDone[s.id] && (
                      <div style={{ marginTop: 8 }}>
                        <button onClick={() => loadCandidates(s)} className="btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12 }}>
                          {expanded === s.id ? 'Hide coverage options' : 'Who can cover?'}
                        </button>
                        {expanded === s.id && (
                          <div style={{ marginTop: 8, borderTop: '1px dashed #e0e0e0', paddingTop: 8 }}>
                            {cands[s.id]?.loading && <div style={{ fontSize: 12, color: '#757575' }}>Checking availability…</div>}
                            {cands[s.id]?.error && <div style={{ fontSize: 12, color: '#c62828' }}>{cands[s.id].error}</div>}
                            {cands[s.id]?.list && cands[s.id].list.length === 0 && (
                              <div style={{ fontSize: 12, color: '#757575' }}>
                                Nobody at this store is free and available for this window.
                              </div>
                            )}
                            {cands[s.id]?.list && cands[s.id].list.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {cands[s.id].list.map((c) => (
                                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                    <div style={{ flex: 1 }}>
                                      {c.name} <span style={{ color: '#9e9e9e', fontSize: 11 }}>· {c.role}</span>
                                      {c.recent_callouts > 0 && (
                                        <span style={{ color: '#b45309', fontSize: 11 }}> · {c.recent_callouts} recent call-out{c.recent_callouts > 1 ? 's' : ''}</span>
                                      )}
                                      {c.availability_note && (
                                        <span style={{ color: '#b45309', fontSize: 11 }}> · {c.availability_note}</span>
                                      )}
                                    </div>
                                    <button onClick={() => assign(s, c)} disabled={!!busy} className="btn-secondary"
                                      style={{ padding: '3px 10px', fontSize: 12 }}>
                                      Assign
                                    </button>
                                  </div>
                                ))}
                                <button onClick={() => messageToCover(s)} disabled={!!busy} className="btn-primary"
                                  style={{ marginTop: 6, padding: '6px 10px', fontSize: 12 }}>
                                  {busy === `${s.id}:message` ? 'Messaging…'
                                    : `Message all ${cands[s.id].list.length} to cover (first to accept gets it)`}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {recorded && (
            <div style={{ fontSize: 12, color: '#2e7d32', fontWeight: 600 }}>
              ✓ Call-out recorded for {recorded.recorded.length + recorded.skipped.length} day{outDates.length > 1 ? 's' : ''}
              {recorded.skipped.length > 0 ? ` (${recorded.skipped.length} already on file)` : ''}.
              Now find coverage for each shift below.
            </div>
          )}

          {err && <div style={{ fontSize: 12, color: '#c62828' }}>{err}</div>}
        </div>

        <div style={{ padding: 12, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="btn-secondary">
            {recorded ? 'Done' : 'Cancel'}
          </button>
          {!recorded && (
            <button onClick={record} disabled={recording || outDates.length === 0} className="btn-primary">
              {recording ? 'Recording…'
                : outDates.length === 0 ? 'Record call-out'
                : `Record call-out (${outDates.length} day${outDates.length > 1 ? 's' : ''})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
